/**
 * Pure rules for first-contact tips (onboarding/tips.js): which tips may show, which an
 * intent or a surface retires, the persisted state, and the tips as plain help text.
 * No React and no DOM; the hook (hooks/useOnboardingTips.js) supplies what is on screen.
 */

/**
 * Most interrupting tips shown in one visit, so a first session is never a stream of popups. A
 * tip that `answersAction` (the user just opened or did something) neither counts nor is held.
 */
export const MAX_TIPS_PER_VISIT = 4;
/**
 * A tip left unanswered this long steps aside for the rest of the visit (not retired; a `once`
 * tip already is, so it just ends).
 */
export const SNOOZE_AFTER_MS = 30_000;
/** How long an anchor must stay on screen before its tip shows, so passing UI never flashes one. */
export const DWELL_MS = 700;
/** The pause after one tip ends before the next may show, so they never feel back-to-back. */
export const NEXT_TIP_GAP_MS = 1400;

/**
 * localStorage key of the persisted state: `{retired: {[id]: reason}, off, visits, ready}`.
 * `ready` holds the tips whose `afterIntents` the user has done.
 */
export const TIPS_STATE_KEY = "sooth-tips-v1";
/** sessionStorage marker: this tab's visit has been counted. */
export const TIPS_VISIT_KEY = "sooth-tips-visit-counted";
/** sessionStorage: this visit's tips shown and stepped aside, so a reload keeps the visit's quota. */
export const TIPS_VISIT_STATE_KEY = "sooth-tips-visit-state";
/**
 * How long a tip named in another's `after` may stay off a settled page before it is skipped
 * there (a forecast with no triggers has no trigger to point at). Not retired: it still shows
 * wherever its control appears.
 */
export const PREREQ_GRACE_MS = 5000;

const emptyState = () => ({ retired: {}, off: false, visits: 0, ready: {} });

/**
 * The CSS selector for one anchor spec: `{ui}` (optionally one `key` of a repeated control) or
 * `{surface}`, optionally narrowed by `where` (data attributes the element must carry) and
 * `within` a surface.
 * @param {{ui?: string, key?: string, surface?: string, where?: object, within?: string}} spec
 * @returns {string}
 */
export function anchorSelector(spec) {
  const base = spec.ui
    ? `[data-ui="${spec.ui}"]${spec.key ? `[data-ui-key="${spec.key}"]` : ""}`
    : `[data-surface="${spec.surface}"]`;
  const own =
    base +
    Object.entries(spec.where ?? {})
      .map(([attr, value]) => `[${attr}="${value}"]`)
      .join("");
  return spec.within ? `[data-surface="${spec.within}"] ${own}` : own;
}

/**
 * Tips that may show now, in priority order: not retired, not stepped aside this visit, due
 * for this visit, after any `afterIntents`, once every tip in `after` has had its turn (retired,
 * shown or stepped aside this visit, or `skipped` here), with every `whilePresent` surface on the
 * page and no `whileAbsent` one, and — unless it `answersAction` —
 * only while the visit's quota lasts (a tip already shown this visit may come back when its
 * anchor does).
 * @param {object[]} tips
 * @param {{retired: object, snoozed: Set<string>, shownIds: Set<string>, visits: number,
 *   ready?: object, isPresent?: (surface: string) => boolean, skipped?: (id: string) => boolean}} s
 * @returns {object[]}
 */
export function eligibleTips(
  tips,
  { retired, snoozed, shownIds, visits, ready = {}, isPresent = () => true, skipped = () => false },
) {
  // Every tip shown counts except one known to answer an action (an id no longer in the list
  // counts, so the quota never loosens by itself).
  const answering = new Set(tips.filter((t) => t.answersAction).map((t) => t.id));
  const interruptingShown = [...shownIds].filter((id) => !answering.has(id)).length;
  const quotaLeft = interruptingShown < MAX_TIPS_PER_VISIT;
  return tips.filter(
    (t) =>
      !retired[t.id] &&
      !snoozed.has(t.id) &&
      (!t.afterIntents || ready[t.id] === true) &&
      (t.after ?? []).every(
        (id) => retired[id] || shownIds.has(id) || snoozed.has(id) || skipped(id),
      ) &&
      (t.fromVisit ?? 1) <= Math.max(visits, 1) &&
      (t.whilePresent ?? []).every(isPresent) &&
      !(t.whileAbsent ?? []).some(isPresent) &&
      (t.answersAction || quotaLeft || shownIds.has(t.id)),
  );
}

/**
 * Ids of the tips that the intent `name` retires.
 * @param {object[]} tips
 * @param {string} name
 * @returns {string[]}
 */
export function tipsRetiredByIntent(tips, name) {
  return tips.filter((t) => t.retireOn?.intents?.includes(name)).map((t) => t.id);
}

/**
 * The `after` prerequisites that may be skipped now: not yet had their turn, and their control
 * off the page for `PREREQ_GRACE_MS` while a tip that waits on them has its `whilePresent`
 * context and the page is not `loading` (a forecast still running has no triggers yet). Keeps
 * when each went missing in `absentSince` (mutated), resetting it whenever those hold no longer.
 * @param {object[]} tips
 * @param {{retired: object, shownIds: Set<string>, snoozed: Set<string>,
 *   isPresent: (surface: string) => boolean, anchorShown: (id: string) => boolean,
 *   loading: boolean, now: number, absentSince: Map<string, number>}} s
 * @returns {Set<string>}
 */
export function trackSkippablePrereqs(
  tips,
  { retired, shownIds, snoozed, isPresent, anchorShown, loading, now, absentSince },
) {
  const contextFor = new Map();
  for (const t of tips) {
    const ok = !loading && (t.whilePresent ?? []).every(isPresent);
    for (const id of t.after ?? []) contextFor.set(id, contextFor.get(id) || ok);
  }
  const skippable = new Set();
  for (const [id, ok] of contextFor) {
    const turned = retired[id] || shownIds.has(id) || snoozed.has(id);
    if (turned || !ok || anchorShown(id)) {
      absentSince.delete(id);
      continue;
    }
    if (!absentSince.has(id)) absentSince.set(id, now);
    if (now - absentSince.get(id) >= PREREQ_GRACE_MS) skippable.add(id);
  }
  return skippable;
}

/**
 * Whether an intent that began matches an `afterIntents` entry: a name, or `{name, params}`
 * where every listed param must equal the intent's.
 * @param {string|{name: string, params?: object}} spec
 * @param {string} name
 * @param {object} [params]
 * @returns {boolean}
 */
export function intentMatches(spec, name, params = {}) {
  const want = typeof spec === "string" ? { name: spec } : spec;
  if (want.name !== name) return false;
  return Object.entries(want.params ?? {}).every(([k, v]) => params?.[k] === v);
}

/**
 * Ids of the tips the intent `name` (with `params`) makes due.
 * @param {object[]} tips
 * @param {string} name
 * @param {object} [params]
 * @returns {string[]}
 */
export function tipsReadiedByIntent(tips, name, params) {
  return tips
    .filter((t) => t.afterIntents?.some((spec) => intentMatches(spec, name, params)))
    .map((t) => t.id);
}

/**
 * Ids of the tips retired by a control that is now on screen (`retireOn.controls`): narrower than
 * a surface when one surface has modes (Tracking vs Access settings share a panel).
 * @param {object[]} tips
 * @param {(uiId: string) => boolean} isShown
 * @returns {string[]}
 */
export function tipsRetiredByControls(tips, isShown) {
  return tips.filter((t) => t.retireOn?.controls?.some(isShown)).map((t) => t.id);
}

/**
 * Ids of the tips retired by a click on the control `uiId` (`retireOn.clicks`).
 * @param {object[]} tips
 * @param {string} uiId
 * @returns {string[]}
 */
export function tipsRetiredByClick(tips, uiId) {
  return tips.filter((t) => t.retireOn?.clicks?.includes(uiId)).map((t) => t.id);
}

/**
 * Ids of the tips retired by a surface that is now on screen.
 * @param {object[]} tips
 * @param {(surface: string) => boolean} isPresent
 * @returns {string[]}
 */
export function tipsRetiredBySurfaces(tips, isPresent) {
  return tips.filter((t) => t.retireOn?.surfaces?.some(isPresent)).map((t) => t.id);
}

/**
 * `text` split on `**bold**` markers, for rendering.
 * @param {string} text
 * @returns {{text: string, bold: boolean}[]}
 */
export function tipTextParts(text) {
  return text
    .split("**")
    .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
    .filter((p) => p.text);
}

/**
 * The tips as a plain bullet list, for answering "how do I…" questions in the chat.
 * @param {object[]} tips
 * @returns {string}
 */
export function tipHelpText(tips) {
  return tips.map((t) => `- ${t.text.replaceAll("**", "")}`).join("\n");
}

/**
 * The prompt clause that lets the chat answer "how do I…" questions from the tips (with a
 * trailing blank line), or "" when there is no help text.
 * @param {string} helpText
 * @returns {string}
 */
export function howSayerWorksClause(helpText) {
  if (!helpText) return "";
  return `HOW SAYER WORKS (answer questions about using the app from this; describe only these controls and never invent others):\n${helpText}\n\n`;
}

/**
 * The persisted state; the empty state for a missing or corrupt entry; null when the storage
 * cannot be read at all (absent, or blocked so that access throws).
 * @param {Storage|null} storage
 * @returns {{retired: object, off: boolean, visits: number, ready: object}|null}
 */
export function tryReadTipState(storage) {
  let raw;
  try {
    if (!storage) return null;
    raw = storage.getItem(TIPS_STATE_KEY);
  } catch {
    return null;
  }
  try {
    const p = raw ? JSON.parse(raw) : null;
    if (!p || typeof p !== "object") return emptyState();
    const obj = (v) => (v && typeof v === "object" ? { ...v } : {});
    return {
      retired: obj(p.retired),
      off: p.off === true,
      visits: Number.isInteger(p.visits) && p.visits > 0 ? p.visits : 0,
      ready: obj(p.ready),
    };
  } catch {
    return emptyState();
  }
}

/**
 * The persisted state, or the empty state on a missing, corrupt or unreadable entry.
 * @param {Storage|null} storage
 * @returns {{retired: object, off: boolean, visits: number, ready: object}}
 */
export function readTipState(storage) {
  return tryReadTipState(storage) ?? emptyState();
}

/**
 * Read, change and persist the state in one step, so a write never puts back another tab's
 * stale copy (its turn-off, or a tip it retired). When the storage cannot be read, `fallback`
 * (the caller's in-memory state) is changed instead. `persisted` is false when the change did
 * not reach storage, so the caller can stop trusting it for this page.
 * @param {Storage|null} storage
 * @param {(s: {retired: object, off: boolean, visits: number, ready: object}) => void} change
 * @param {{retired: object, off: boolean, visits: number, ready: object}|null} [fallback]
 * @returns {{state: {retired: object, off: boolean, visits: number, ready: object},
 *   persisted: boolean}}
 */
export function updateTipState(storage, change, fallback = null) {
  const read = tryReadTipState(storage);
  const s = read ?? fallback ?? emptyState();
  change(s);
  return { state: s, persisted: read !== null && writeTipState(s, storage) };
}

/**
 * Persist the state. Quota and private-mode errors are swallowed; the result says whether it
 * was written.
 * @param {{retired: object, off: boolean, visits: number, ready: object}} state
 * @param {Storage|null} storage
 * @returns {boolean}
 */
export function writeTipState(state, storage) {
  try {
    if (!storage) return false;
    storage.setItem(TIPS_STATE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

/**
 * This visit's tips shown and stepped aside, from the tab session; empty when unreadable.
 * @param {Storage|null} session
 * @returns {{shownIds: Set<string>, snoozed: Set<string>}}
 */
export function readVisitState(session) {
  try {
    const p = JSON.parse(session?.getItem(TIPS_VISIT_STATE_KEY) || "null");
    const ids = (v) => new Set(Array.isArray(v) ? v.filter((x) => typeof x === "string") : []);
    return { shownIds: ids(p?.shown), snoozed: ids(p?.snoozed) };
  } catch {
    return { shownIds: new Set(), snoozed: new Set() };
  }
}

/**
 * Persist this visit's tips shown and stepped aside. Best-effort.
 * @param {{shownIds: Set<string>, snoozed: Set<string>}} v
 * @param {Storage|null} session
 */
export function writeVisitState(v, session) {
  try {
    session?.setItem(
      TIPS_VISIT_STATE_KEY,
      JSON.stringify({ shown: [...v.shownIds], snoozed: [...v.snoozed] }),
    );
  } catch {
    /* best-effort */
  }
}

/**
 * The state with this tab's visit counted: once per tab session, marked in `session`.
 * @param {{retired: object, off: boolean, visits: number, ready: object}} state
 * @param {Storage|null} session
 * @returns {{retired: object, off: boolean, visits: number, ready: object}}
 */
export function countVisit(state, session) {
  try {
    if (session?.getItem(TIPS_VISIT_KEY)) return state;
    session?.setItem(TIPS_VISIT_KEY, "1");
  } catch {
    return state;
  }
  return { ...state, visits: state.visits + 1 };
}
